import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { AppThemeProvider } from './context/AppThemeContext';
import { ProtectedRoute, ErrorBoundary } from './components/common';
import { Layout } from './components/layout';
import { isAppDomain } from './utils/domain';

// Auth Pages
import { Login, ForgotPassword, ResetPassword, AcceptInvitation, OAuthConsent } from './pages/Auth';

// Dashboard
import { Dashboard } from './pages/Dashboard';

// User Pages
import { UserManagement, InviteUser, EditUser } from './pages/Users';

// Settings Pages
import { UserProfile, OrganizationSettings } from './pages/Settings';

// OKR Pages
import { OKRList, CreateOKR, OKRDetails, OKRApprovalHistory, AlignmentMap } from './pages/OKR';

// Check-ins Page
import { CheckInsPage } from './pages/CheckIns';

// Approval Pages
import { PendingApprovals } from './pages/Approvals';

// Admin Pages
import { AdminPage } from './pages/Admin';
import { SuperAdminPage } from './pages/SuperAdmin';

// Reports Pages
import { ReportsPage } from './pages/Reports';

// Performance Pages
import { PerformancePage } from './pages/Performance';

// Setup Page
import { SetupPage } from './pages/Setup/SetupPage';

// Landing Pages
import { LandingPage, FeaturesPage } from './pages/Landing';
import { ConsultPage } from './pages/Landing/ConsultPage';
import { LandingLayout } from './components/landing/LandingLayout';

// Legal Pages
import { PrivacyPolicy, TermsOfUse } from './pages/Legal';

// Analytics
import { useGoogleAnalytics } from './hooks';

// The marketing routes below are guarded by isAppDomain and NOT by isMarketingDomain:
// localhost is neither, and it must fall through to the landing page rather than be sent
// to /login.
//
//   okrhub.hr4india.com, staging.okrhub.hr4india.com → OKR Hub app (redirect to /login)
//   hr4india.com, www.hr4india.com, localhost        → marketing landing page

/**
 * Home route handler - shows landing page on marketing domain,
 * redirects to login on OKR Hub app domain
 */
const HomeRoute: React.FC = () => {
  if (isAppDomain()) {
    return <Navigate to="/login" replace />;
  }
  return (
    <LandingLayout>
      <LandingPage />
    </LandingLayout>
  );
};

/**
 * Consult route handler - only available on marketing domain
 */
const ConsultRoute: React.FC = () => {
  if (isAppDomain()) {
    return <Navigate to="/login" replace />;
  }
  return (
    <LandingLayout>
      <ConsultPage />
    </LandingLayout>
  );
};

/**
 * Features route handler - only available on marketing domain
 */
const FeaturesRoute: React.FC = () => {
  if (isAppDomain()) {
    return <Navigate to="/login" replace />;
  }
  return (
    <LandingLayout>
      <FeaturesPage />
    </LandingLayout>
  );
};

/**
 * Privacy Policy route - available on all domains
 */
const PrivacyRoute: React.FC = () => {
  return <PrivacyPolicy />;
};

/**
 * Terms of Use route - available on all domains
 */
const TermsRoute: React.FC = () => {
  return <TermsOfUse />;
};

/**
 * Google Analytics tracker component - tracks page views on route changes
 * Only active on hr4india.com marketing site
 */
const GoogleAnalyticsTracker: React.FC = () => {
  useGoogleAnalytics();
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <GoogleAnalyticsTracker />
            <AuthProvider>
              <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/home" element={<HomeRoute />} />
            <Route path="/consult" element={<ConsultRoute />} />
            <Route path="/client" element={<ConsultRoute />} />
            <Route path="/features" element={<FeaturesRoute />} />
            <Route path="/privacy-policy" element={<PrivacyRoute />} />
            <Route path="/terms-of-use" element={<TermsRoute />} />
            <Route path="/login" element={<Login />} />
            {/* OAuth consent. Public by design: a signed-out user must reach this page and
                sign in ON it. Wrapping it in ProtectedRoute would bounce them to /login,
                which navigates to /dashboard on success and strands the pending request. */}
            <Route path="/oauth/consent" element={<OAuthConsent />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/setup" element={<SetupPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* User Profile */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Check-ins */}
            <Route
              path="/check-ins"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CheckInsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* OKR Routes */}
            <Route
              path="/okrs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OKRList />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/okrs/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateOKR />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/okrs/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateOKR />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/okrs/alignment"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AlignmentMap />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/okrs/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OKRDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/okrs/:id/approval-history"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OKRApprovalHistory />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Approvals - Manager only */}
            <Route
              path="/approvals/pending"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <PendingApprovals />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* User Management - Admin/Manager only */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/invite"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <InviteUser />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/:id"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <EditUser />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/:id/edit"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <EditUser />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Organization Settings - Admin only */}
            <Route
              path="/organization"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <OrganizationSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Admin Panel - Admin only */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <AdminPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Reports - Manager and Admin */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredRole="manager">
                  <Layout>
                    <ReportsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Performance - All authenticated users */}
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PerformancePage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Super Admin Panel - Super Admin only */}
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Layout>
                    <SuperAdminPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home (or login on OKR Hub domain) */}
            <Route path="*" element={isAppDomain() ? <Navigate to="/login" replace /> : <Navigate to="/" replace />} />
            </Routes>
              <Toaster position="top-right" />
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
        {/* HR4India Branding Logo */}
        <img
          src="/hrfi.png"
          alt="HR4India"
          style={{
            position: 'fixed',
            bottom: '12px',
            right: '12px',
            width: '48px',
            height: '32px',
            objectFit: 'contain',
            opacity: 0.8,
            zIndex: 9999,
          }}
        />
      </AppThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
